import type { Editor } from '@tiptap/core';

type TextRange = { from: number; to: number };

function isMarkActiveInRange(
  editor: Editor,
  markName: string,
  range: TextRange
): boolean {
  const markType = editor.schema.marks[markName];
  if (!markType) return false;

  // Match TipTap `isActive(mark)` / `isMarkActive` semantics for a non-empty
  // range: the mark must cover the full text extent, not merely appear
  // somewhere (`rangeHasMark`). Partial coverage keeps the toggle OFF so
  // clicking bolds the rest instead of un-bolding the one marked word.
  let selectionRange = 0;
  let matchedRange = 0;
  editor.state.doc.nodesBetween(range.from, range.to, (node, pos) => {
    if (!node.isText && node.marks.length === 0) {
      return;
    }
    const relativeFrom = Math.max(range.from, pos);
    const relativeTo = Math.min(range.to, pos + node.nodeSize);
    const size = relativeTo - relativeFrom;
    selectionRange += size;
    if (markType.isInSet(node.marks)) {
      matchedRange += size;
    }
  });
  return selectionRange > 0 && matchedRange >= selectionRange;
}

function isNodeActiveInRange(
  editor: Editor,
  nodeName: string,
  range: TextRange
): boolean {
  const nodeType = editor.schema.nodes[nodeName];
  if (!nodeType) return false;

  let found = false;
  editor.state.doc.nodesBetween(range.from, range.to, (node) => {
    if (node.type === nodeType) {
      found = true;
      return false;
    }
  });
  return found;
}

/** Section kebab formatting row: active toggles for a fixed body range. */
export function selectSectionFormattingStates(editor: Editor, range: TextRange | null) {
  if (!range) {
    return {
      isBold: false,
      isItalic: false,
      isUnderline: false,
      isBulletList: false,
      isOrderedList: false,
    };
  }

  return {
    isBold: isMarkActiveInRange(editor, 'bold', range),
    isItalic: isMarkActiveInRange(editor, 'italic', range),
    isUnderline: isMarkActiveInRange(editor, 'underline', range),
    isBulletList: isNodeActiveInRange(editor, 'bulletList', range),
    isOrderedList: isNodeActiveInRange(editor, 'orderedList', range),
  };
}

/** Bubble text menu: full formatting row (v1 + vMarkdown block editors). */
export function selectFullTextToolbarFormatStates(ctx: { editor: Editor }) {
  return {
    isHeading: ctx.editor.isActive('heading'),
    isBold: ctx.editor.isActive('bold'),
    isItalic: ctx.editor.isActive('italic'),
    isStrike: ctx.editor.isActive('strike'),
    isSubscript: ctx.editor.isActive('subscript'),
    isSuperscript: ctx.editor.isActive('superscript'),
    isAlignLeft: ctx.editor.isActive({ textAlign: 'left' }),
    isAlignCenter: ctx.editor.isActive({ textAlign: 'center' }),
    isAlignRight: ctx.editor.isActive({ textAlign: 'right' }),
    isAlignJustify: ctx.editor.isActive({ textAlign: 'justify' }),
    isBulletList: ctx.editor.isActive('bulletList'),
    isOrderedList: ctx.editor.isActive('orderedList'),
    isUnderline: ctx.editor.isActive('underline'),
    isCodeBlock: ctx.editor.isActive('codeBlock'),
  };
}

/** Bubble text menu: JSON note editor (subset of controls). */
export function selectMinimalTextToolbarFormatStates(ctx: { editor: Editor }) {
  return {
    isBold: ctx.editor.isActive('bold'),
    isItalic: ctx.editor.isActive('italic'),
  };
}
